import React, { useEffect, useState } from "react";
import { isEnabled } from "@/utils/featureFlags";
import { Button, ButtonGroup, Icon } from "@/aible";
import styles from "@/components/LanguageModelArtifact/style.module.css";
import { useHistory } from "react-router-dom";
import SearchInput from "components/Inputs/SearchInput/SearchInput";
import Tooltip from "components/Inputs/Tooltip";
import ModelUpload from "@/components/LanguageModelArtifact/ModelUpload";
import ProvisionModelModal from "@/components/LanguageModelArtifact/ProvisionModelModal";
import { FineTuningExecutionType } from "@/types/content_generation";
import { useDispatch, useSelector } from "@/types/store";
import sparklesPurpleImg from "images/admin_images/sparkles-purple.svg";
import FileUpArrowImg from "images/file-arrow-up-light.svg";
import GeminiModelIcon from "images/Model Icons/gemini-logo.svg";
import HuggingFaceModelIcon from "images/Model Icons/hugging-face-logo.svg";
import VertexAiModelIcon from "images/Model Icons/vertex-ai-seeklogo.svg";
import AWSModelIcon from "images/Model Icons/aws.svg";
import LlamaModelIcon from "images/Model Icons/llama.svg";
import MistralModelIcon from "images/Model Icons/mistral-logo.svg";
import OpenAIModelIcon from "images/Model Icons/openai 1.svg";
import storefrontIcon from "images/store-sharp-purple.svg";
import * as aiServiceReducer from "@/containers/AiService/reducer";
import { format, parseISO } from "date-fns";
import * as foldersReducer from "@/containers/Folders/reducer";
import * as fineTuningReducer from "@/containers/FineTuning/reducer";
import NoModelsAvailableTile from "@/components/LanguageModelArtifact/Models/NoModelsAvailable";
import TrainingJobsTable from "@/components/LanguageModelArtifact/Models/TrainingJobsTable";
import { debounce } from "throttle-debounce";
import FineTuneTrainingModal from "@/components/LanguageModelArtifact/FineTuneTrainingModal";
import * as contentGenerationReducer from "containers/ContentGeneration/reducer";
import ModelEvaluationModal from "@/components/LanguageModelArtifact/ModelEvaluationModal";
import ProvisionedModelsTable from "@/components/LanguageModelArtifact/Models/ProvisionedModelsTable";
import ModelEvaluationTable from "@/components/LanguageModelArtifact/ModelEvaluation/ModelEvaluationTable";
import ModelEvaluationDetailsModal from "@/components/LanguageModelArtifact/ModelEvaluation/ModelEvaluationDetailsModal";
export var ModelsView;
(function (ModelsView) {
    ModelsView["PROVISIONED_MODELS"] = "PROVISIONED_MODELS";
    ModelsView["PROVISIONED_EMBEDDINGS_MODELS"] = "PROVISIONED_EMBEDDINGS_MODELS";
    ModelsView["TRAINING_JOBS"] = "TRAINING_JOBS";
    ModelsView["MODEL_EVALUATIONS"] = "MODEL_EVALUATIONS";
})(ModelsView || (ModelsView = {}));
const Models = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const aiServiceModels = useSelector((state) => aiServiceReducer.getModels(state));
    const aiServiceEmbeddingsModels = useSelector((state) => aiServiceReducer.getModels(state, "EMBEDDING"));
    const aiServiceConnections = useSelector((state) => aiServiceReducer.getConnections(state));
    const selectedFolderId = useSelector((state) => foldersReducer.getSelectedFolder(state));
    const fineTuningExecutionsRaw = useSelector((state) => fineTuningReducer.getFineTunings(state));
    const exampleSetSelector = useSelector((state) => contentGenerationReducer.getExampleSets(state));
    const exampleSets = contentGenerationReducer.filterExampleSets(exampleSetSelector, selectedFolderId);
    const [showProvisionModelModal, updateShowProvisionModelModal] = useState(false);
    const [selectedDataset, updateSelectedDataset] = useState(undefined);
    const [selectedModel, updateSelectedModel] = useState(undefined);
    const [showTrainingModal, updateShowTrainingModal] = useState(false);
    const [modelView, updateModelView] = useState(ModelsView.PROVISIONED_MODELS);
    const [isRefreshingModels, updateIsRefreshingModels] = useState(false);
    const [searchFilter, updateSearchFilter] = useState("");
    const [showModelEvaluation, setShowModelEvaluation] = useState(false);
    const [showEvaluatorDetails, setShowEvaluatorDetails] = useState(false);
    const [selectedModelEvaluation, updateSelectedModelEvaluation] = useState(undefined);
    const [selectedEvaluatorId, updateSelectedEvaluatorId] = useState(undefined);
    const handleModelEvaluationAction = (modelId) => {
        updateSelectedModelEvaluation(modelId);
        setShowModelEvaluation(true);
    };
    const handleEvaluatorAction = (evaluatorId) => {
        updateSelectedEvaluatorId(evaluatorId);
        setShowEvaluatorDetails(true);
    };
    const handleFineTuneAction = (dataset) => {
        updateSelectedDataset(dataset);
        updateSelectedModel(undefined);
        updateShowTrainingModal(true);
    };
    useEffect(() => {
        if (!showModelEvaluation) {
            updateSelectedModelEvaluation(undefined);
        }
    }, [showModelEvaluation]);
    const debouncedSearchFilter = debounce(200, updateSearchFilter);
    const folderConnections = Object.values(aiServiceConnections)?.filter((conn) => conn?.folder_id === selectedFolderId || conn?.folder_id === null);
    const fineTuningExecutions = Object.values(fineTuningExecutionsRaw)?.filter((e) => folderConnections?.some((conn) => conn.models?.some((m) => m?.version_id === e?.base_model_version_id)));
    const fineTuningExecutionsInfo = fineTuningExecutions
        ?.map((execution) => {
        const createdDate = parseISO(execution?.created_date + "Z");
        const formattedDate = format(createdDate, "MM/dd/yyyy, hh:mm aa");
        const baseModelVersionId = execution.base_model_version_id;
        const baseModel = Object.values(aiServiceModels).find((x) => x.version_id == baseModelVersionId);
        if (baseModel === undefined) {
            return undefined;
        }
        return {
            name: execution.name,
            owner: execution.created_by?.fullname,
            createdDate: execution.created_date && formattedDate,
            model_id: baseModel.external_id,
            source: baseModel.name,
            llm: execution,
            status: execution.status,
            type: execution.type,
            id: execution.id
        };
    })
        .filter((e) => e !== undefined)
        .reverse();
    const provisionedModelsInfo = fineTuningExecutionsInfo.filter((e) => e.type === FineTuningExecutionType.TRAINING);
    const evaluationModelsInfo = fineTuningExecutionsInfo.filter((e) => e.type === FineTuningExecutionType.EVALUATION);
    const trainingJobsCount = provisionedModelsInfo?.length || 0;
    const evaluationJobsCount = evaluationModelsInfo?.length || 0;
    const modelsCount = Object.keys(aiServiceModels)?.length || 0;
    const modelButtonText = modelsCount === 1 ? `${modelsCount} Available Model` : `${modelsCount} Available Models`;
    const embeddingsModelsCount = Object.keys(aiServiceEmbeddingsModels)?.length || 0;
    const embeddingsModelButtonText = embeddingsModelsCount === 1
        ? `${embeddingsModelsCount} Available Embeddings Model`
        : `${embeddingsModelsCount} Available Embeddings Models`;
    const trainingJobsButtonText = trainingJobsCount === 1 ? `${trainingJobsCount} Training Job` : `${trainingJobsCount} Post Training Jobs`;
    const modelEvaluationsButtonText = `${evaluationJobsCount} Model Evaluation${evaluationJobsCount !== 1 && "s"}`;
    const isNoModelsAvailable = (modelView === ModelsView.PROVISIONED_EMBEDDINGS_MODELS && embeddingsModelsCount === 0) ||
        (modelView === ModelsView.PROVISIONED_MODELS && modelsCount === 0) ||
        (modelView === ModelsView.TRAINING_JOBS && trainingJobsCount === 0) ||
        (modelView === ModelsView.MODEL_EVALUATIONS && evaluationJobsCount === 0);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.fineTuningModelActions },
            React.createElement("div", { className: styles.fineTuningModelActionsContainer },
                isEnabled("add-llm-through-api") && (React.createElement(Button, { onClick: () => {
                        updateShowProvisionModelModal(true);
                    }, style: { marginRight: "20px" } },
                    React.createElement("span", { style: { marginRight: "14px" } }, "Add Model"),
                    React.createElement("img", { className: styles.modelButtonLogo, src: GeminiModelIcon }),
                    React.createElement("img", { className: styles.modelButtonLogo, src: OpenAIModelIcon }),
                    React.createElement("img", { className: styles.modelButtonLogo, src: LlamaModelIcon }),
                    React.createElement("img", { className: styles.modelButtonLogo, src: AWSModelIcon }),
                    React.createElement("img", { className: styles.modelButtonLogo, src: MistralModelIcon }),
                    React.createElement("img", { className: styles.modelButtonLogo, src: HuggingFaceModelIcon }),
                    React.createElement("img", { className: styles.modelButtonLogo, src: VertexAiModelIcon, style: { marginRight: "0px" } }))),
                isEnabled("custom-model-upload") && (React.createElement(ModelUpload, { buttonType: true, buttonText: "Upload Custom LoRA Weights", buttonStyle: { marginRight: "20px" }, buttonLogo: FileUpArrowImg, buttonLogoClass: styles.uploadButtonIcon })),
                React.createElement(Button, { onClick: () => {
                        updateSelectedDataset(undefined);
                        updateSelectedModel(undefined);
                        updateShowTrainingModal(true);
                    }, style: { marginRight: "20px" } },
                    React.createElement("img", { className: styles.buttonIcon, src: sparklesPurpleImg }),
                    "Post Train A Model"),
                isEnabled("community-market") && (React.createElement(Button, { onClick: () => {
                        history.push("/community_market");
                    } },
                    React.createElement("img", { className: styles.buttonIcon, src: storefrontIcon }),
                    "Search Marketplace")))),
        React.createElement("div", { className: styles.fineTuningDataPanel },
            React.createElement("div", { className: styles.fineTuningTableHeader },
                React.createElement("div", null,
                    React.createElement(ButtonGroup, null,
                        React.createElement(Button, { onClick: () => updateModelView(ModelsView.PROVISIONED_MODELS), active: modelView === ModelsView.PROVISIONED_MODELS }, modelButtonText),
                        embeddingsModelsCount > 0 && (React.createElement(Button, { onClick: () => updateModelView(ModelsView.PROVISIONED_EMBEDDINGS_MODELS), active: modelView === ModelsView.PROVISIONED_EMBEDDINGS_MODELS }, embeddingsModelButtonText)),
                        React.createElement(Button, { onClick: () => updateModelView(ModelsView.TRAINING_JOBS), active: modelView === ModelsView.TRAINING_JOBS }, trainingJobsButtonText),
                        React.createElement(Button, { onClick: () => updateModelView(ModelsView.MODEL_EVALUATIONS), active: modelView === ModelsView.MODEL_EVALUATIONS }, modelEvaluationsButtonText))),
                React.createElement("div", { className: styles.fineTuningActions },
                    modelView === ModelsView.PROVISIONED_MODELS && (React.createElement(Button, { type: "secondary", onClick: () => setShowModelEvaluation(true) }, "Evaluate Model")),
                    React.createElement(Tooltip, { overlay: "Refresh Model List" },
                        React.createElement(Button, { style: {
                                padding: "9px"
                            }, onClick: () => {
                                updateIsRefreshingModels(true);
                                dispatch(aiServiceReducer.refreshModelsList()).finally(() => {
                                    updateIsRefreshingModels(false);
                                });
                            }, isLoading: isRefreshingModels },
                            React.createElement(Icon, { type: "refresh", size: 13 }))),
                    modelsCount > 0 && (React.createElement(SearchInput, { value: searchFilter, onChange: (val) => debouncedSearchFilter(val), style: { height: "37px" } })))),
            !isNoModelsAvailable && (React.createElement("div", { className: styles.fineTuningTableContainer },
                modelView === ModelsView.PROVISIONED_MODELS && modelsCount > 0 && (React.createElement(ProvisionedModelsTable, { type: "CONTENT_GENERATION", searchFilter: searchFilter, handleModelEvaluationAction: handleModelEvaluationAction, handleFineTuneAction: handleFineTuneAction, handleEvaluatorAction: handleEvaluatorAction })),
                modelView === ModelsView.PROVISIONED_EMBEDDINGS_MODELS && embeddingsModelsCount > 0 && (React.createElement(ProvisionedModelsTable, { type: "EMBEDDING", searchFilter: searchFilter, handleModelEvaluationAction: handleModelEvaluationAction, handleFineTuneAction: handleFineTuneAction, handleEvaluatorAction: handleEvaluatorAction })),
                modelView === ModelsView.TRAINING_JOBS && trainingJobsCount > 0 && (React.createElement(TrainingJobsTable, { data: provisionedModelsInfo, searchFilter: searchFilter })),
                modelView === ModelsView.MODEL_EVALUATIONS && evaluationJobsCount > 0 && (React.createElement(ModelEvaluationTable, { data: evaluationModelsInfo, searchFilter: searchFilter, handleEvaluatorAction: handleEvaluatorAction })))),
            isNoModelsAvailable && (React.createElement("div", { className: styles.noModelsAvailableContainer },
                React.createElement(NoModelsAvailableTile, { modelViewType: modelView })))),
        isEnabled("add-llm-through-api") && (React.createElement(ProvisionModelModal, { showModal: showProvisionModelModal, updateShowModal: updateShowProvisionModelModal })),
        React.createElement(FineTuneTrainingModal, { showModal: showTrainingModal, updateShowModal: updateShowTrainingModal, dataset: selectedDataset, updateSelectedDataset: updateSelectedDataset, datasetOptions: exampleSets, selectedModel: selectedModel, updateSelectedModel: updateSelectedModel }),
        React.createElement(ModelEvaluationModal, { open: showModelEvaluation, onOpenChange: setShowModelEvaluation, defaultModelId: selectedModelEvaluation }),
        !!selectedEvaluatorId && (React.createElement(ModelEvaluationDetailsModal, { open: showEvaluatorDetails, onOpenChange: setShowEvaluatorDetails, selectedEvaluatorId: selectedEvaluatorId }))));
};
export default Models;
